/**
 * @file    percentageSettingsUtils.ts
 * @scope   SHARED
 * @summary Builds the Percentage calculation's advanced-settings options and defaults from a
 *          field's Responses (rating-based value selection).
 * @consumes FieldModel, PercentageSettingsModel, PercentageValueOption
 * @produces `fieldHasRatings`, `buildRatingOptions`, `buildAvailablePercentageOptions`,
 *           `buildDefaultPercentageSettings`
 * @sideEffects None — pure.
 * @notes Label resolution order: ShortText → Text → Label → numeric Rating string. Flags
 *        (DK/NA/REF) are stored as strings.
 */
import {
  FieldModel,
  PercentageSettingsModel,
  PercentageValueOption,
} from 'models/Enterprise/FieldsManagerModel';

/**
 * Returns true if the field has rating-based response values (Responses array with a Rating property).
 */
export const fieldHasRatings = (field: FieldModel): boolean =>
  Array.isArray(field?.responses) &&
  field.responses.length > 0 &&
  field.responses[0]?.rating != null;

/**
 * Builds multi-select options from a rating-based field's Responses.
 * Label resolution: ShortText → Text → Label → numeric Rating string as fallback.
 * Flags (DK/NA/REF) are stored as strings.
 */
export const buildRatingOptions = (field: FieldModel): PercentageValueOption[] =>
  (field?.responses ?? []).map((r: any) => ({
    value: r.flag ? String(r.flag) : r.rating, // Store flags as strings
    label: r.choice ?? r.shortText ?? r.Text ?? r.Label ?? String(r.rating),
    path: r.flag ? r.flag : 'rating',
  }));

/**
 * Builds multi-select options for fields without rating values.
 * Uses field.options or field.responses, storing the id as the value.
 * Flags (DK/NA/REF) are stored as strings.
 */
export const buildNonRatingOptions = (field: FieldModel): PercentageValueOption[] =>
  (field?.options ?? field?.responses ?? []).map((o: any) => ({
    id: o.id,
    value: o.flag ? String(o.flag) : String(o.id ?? o.cxChoiceGuid ?? o.value),
    label: o.choice ?? o.label ?? o.Label ?? o.shortText ?? o.Text ?? String(o.value),
    path: o.flag ? o.flag : 'id',
  }));

/**
 * Builds boolean Yes/No options for SimpleBoolean fields.
 * Yes = true (boolean), No = false (boolean)
 */
export const buildBooleanOptions = (): PercentageValueOption[] => [
  { value: true, label: 'Yes', path: '' },
  { value: false, label: 'No', path: '' },
];

/**
 * Returns the two options with the highest numeric value (top 2 ratings).
 */
export const getTopTwoOptions = (options: PercentageValueOption[]): PercentageValueOption[] =>
  [...options].sort((a, b) => Number(b.value) - Number(a.value)).slice(0, 2);

/**
 * Builds the default PercentageSettingsModel for a field when a Percentage or NPS
 * calculation type is first selected in FieldsManager.
 *
 * Rules:
 * - SimpleBoolean → pre-select 'Yes' (true); path = ''
 * - Field has ratings  → pre-select top 2 rating options; path = field.path
 * - Field has no ratings → empty selection; path = 'id'
 */
export const buildDefaultPercentageSettings = (field: FieldModel): PercentageSettingsModel => {
  // SimpleBoolean: default to 'Yes' (true) selected
  if (field?.typeName === 'SimpleBoolean') {
    return {
      path: '',
      selectedValues: [{ value: true, label: 'Yes', path: '' }],
    };
  }

  if (fieldHasRatings(field)) {
    const options = buildRatingOptions(field);
    return {
      path: field.path ?? '',
      selectedValues: getTopTwoOptions(options),
    };
  }

  return {
    path: 'id',
    selectedValues: [], // default to none selected for non-rating fields
  };
};

/**
 * Builds all available options for the multi-select depending on the field type.
 * SimpleBoolean → Yes/No boolean options
 * Rating fields → rating-based options
 * Other fields → id-based options
 */
export const buildAvailablePercentageOptions = (field: FieldModel): PercentageValueOption[] => {
  if (field?.typeName === 'SimpleBoolean') return buildBooleanOptions();
  return fieldHasRatings(field) ? buildRatingOptions(field) : buildNonRatingOptions(field);
};
